import * as React from 'react';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { none } from 'ramda';
import useTablePage from '../hooks/useTablePage';
import { updateProject } from '../api/projects';
import { getCustomActivities } from '../api/activities';
import { US_STATES } from '../constants/usStates';
import { useDispatch } from 'react-redux';
import { apiSlice } from '../redux/rtkQuery/apiSlice';
import ActionsPanel from '../organisms/ActionsPanel';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import {
  InputAutocompletePlaces,
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  ProjectDetails,
  ButtonWithLoader,
  ModalDefaultDraggable,
} from '../molecules';
import { zipCode } from '../regex';
import ModalInfo from '../molecules/ModalInfo';
import { createUser, getUsers, getUsersByProject, getUsersCustom, UserType } from '../api/users';
import { ButtonMain } from '../atoms';
import UsersForm from './UsersForm';
import AddContactWizard from './AddContactWizard';
import EboxLookup from '../molecules/EboxLookup';
import ContactSearchList from '../organisms/ContactSearchList';
import { GET_ALL_QUERY_PARAMS, InspectionStatuses } from '../constants';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../constants/forms';
import Table from '../organisms/Table';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import { getContactTypeList } from '../api/contactType';
import JurisdictionChangeConfirmModal from './JurisdictionChangeConfirmModal';

const labels = {
  address: 'Address',
  street: 'Street',
  city: 'City',
  state: 'State',
  zipCode: 'Zip',
  jurisdiction: 'Jurisdiction',
  franchise: 'Franchise',
  manager: 'Project Manager',
  contacts: 'Contacts',
  name: 'Project Name',
  project_type: 'Project Type',
};

const validationSchema = yup.object().shape({
  address: yup.string().required().label(labels.address),
  state: yup.string().required().label(labels.state),
  street: yup.string().required().label(labels.street),
  city: yup.string().required().label(labels.city),
  zipCode: yup
    .string()
    .matches(zipCode, 'Invalid Code number. The code number must be in format 55555 or 55555-5555')
    .required()
    .label(labels.zipCode),
  name: yup.string().required().label(labels.name),
  jurisdiction: yup.object().required().typeError('Select a Jurisdiction from the list').label(labels.jurisdiction),
  manager: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Project Manager from the list')
    .nullable()
    .label(labels.manager),
  contacts: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .required()
    .typeError('Select a Contact Names from the list')
    .nullable()
    .label(labels.contacts),
  project_type: yup.string().nullable().required('Please select a project type').label('Project Type'),
});

const ProjectEditForm = ({
  editMode,
  initialValues,
  changeMode,
  jurisdiction,
  handleCloseEditProject,
  HandleAddContactModal,
  updateTable,
  manager,
  contacts,
  hideButtons,
}: Props) => {
  const dispatch = useDispatch();
  const {
    createOrUpdate,
    modalSuccess,
    modalError,
    isApiSuccess,
    successResponse,
    handleCloseModalInfo,
    modalErrorTitle,
    handleAdd,
    isModalOpen,
    modalTitle,
    handleCloseModal,
    values,
    innerModalErrors,
    setSearch,
    search,
    // tableManuallyUpdate,
  } = useTablePage({
    createEndpoint: createUser,
    modalCreateTitle: 'Add Contact',
    updateEndpoint: updateProject,
  });

  const { register, handleSubmit, setError, errors, control, setValue, getValues, watch, formState, clearErrors, reset, trigger } = useForm(
    {
      resolver: yupResolver(validationSchema),
      defaultValues: {
        ...initialValues,
        project_type: initialValues?.project_type || initialValues?.projectType || null,
      },
    },
  );

  const [checkingJurisdiction, setCheckingJurisdiction] = React.useState(false);
  const [jurisdictionWarningOpen, setJurisdictionWarningOpen] = React.useState(false);
  const pendingJurisdictionRef = React.useRef<any>(null);

  const handleJurisdictionSelect = async (selected, commit: (value: any) => void) => {
    const initialId = initialValues?.jurisdiction?.id;
    if (!selected || selected.id === initialId) {
      commit(selected);
      return;
    }
    setCheckingJurisdiction(true);
    try {
      const permits = initialValues?.permits || [];
      const fetchPermitInspections = p =>
        getCustomActivities({ permit_id: p.id, event_type: 'Inspection', limit: 1000 }).catch(() => null);
      const responses = await Promise.all(permits.map(fetchPermitInspections));
      const warnStatuses = [InspectionStatuses.SCHEDULED, InspectionStatuses.NOT_SCHEDULED];
      const hasScheduled = responses.some(r =>
        (r?.data?.results || []).some(i => warnStatuses.includes(i.statusName)),
      );
      if (hasScheduled) {
        pendingJurisdictionRef.current = { selected, commit };
        setJurisdictionWarningOpen(true);
      } else {
        commit(selected);
      }
    } finally {
      setCheckingJurisdiction(false);
    }
  };

  const confirmJurisdictionChange = () => {
    pendingJurisdictionRef.current?.commit(pendingJurisdictionRef.current.selected);
    pendingJurisdictionRef.current = null;
    setJurisdictionWarningOpen(false);
  };

  const cancelJurisdictionChange = () => {
    pendingJurisdictionRef.current = null;
    setJurisdictionWarningOpen(false);
  };

  React.useEffect(() => {
    register({ name: 'project_type' });
    register({ name: 'eboxConfirmationNumber' });
  }, [register]);

  React.useEffect(() => {
    if (initialValues && (initialValues.project_type || initialValues.projectType)) {
      reset({
        ...initialValues,
        project_type: initialValues.project_type || initialValues.projectType || null,
      });
    }
  }, [initialValues, reset]);

  const submitWithServerErrors = async data => {
    try {
      await createOrUpdate(data);
    } catch (err) {
      const apiErrors = err?.response?.data;
      if (apiErrors?.name) {
        setError('name', {
          type: 'server',
          message: Array.isArray(apiErrors.name) ? apiErrors.name[0] : String(apiErrors.name),
        });
      }
      throw err;
    }
  };

  // Add user functionality starts from here
  const [addUserModal, setAddUserModal] = React.useState(false);
  const [clientType, setClientType] = React.useState();
  const [initialSelectedRows, setInitialSelectedRows] = React.useState({});
  const userColumns = column => {
    if (
      [
        'id',
        'firstName',
        'lastName',
        'phoneNumber',
        'address',
        'city',
        'state',
        'clientType',
        'phoneNumber',
        'parentCompany',
        'street',
        'mobilePhoneNumber',
        'userRole',
        'zipCode',
        'isEpsuperadmin',
        'isInvitationSent',
        'licenses',
        'boLicense',
        'archEngLicense',
        'prNumber',
        'isActive',
        'isDeleted',
        'trade',
        'tradesNames',
        'franchise',
        'franchiseNames',
        'avatar',
        'createdAt',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'companyName':
        return 'Company Name';
      case 'email':
        return 'Email';
      case 'name':
        return 'Name';
      // case 'contactType':
      //   return 'Contact Type';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const userColumnOrder = [
    'name',
    // 'contactType',
    'email',
    'companyName',
  ];
  const [isAddContactBtnLoading, setIsAddContactBtnLoading] = React.useState(false);
  const [initialContact, setInitialContact] = React.useState(getValues('contacts'));
  const [addNewUserModalOpen, setAddNewUserModalOpen] = React.useState(false);
  const [addContactModalTitle, setAddContactModalTitle] = React.useState('Add Contacts');

  const isInitialAddUserModal = React.useRef(true);
  React.useEffect(() => {
    if (isInitialAddUserModal.current) {
      isInitialAddUserModal.current = false;
      return;
    }
    setSearch('');
  }, [addUserModal]);
  // const newData = React.useMemo(() => data, [data])
  const clientTypeChange = client => {
    setClientType(client);
  };
  const modifiedGetUsers = params => {
    const searchTerm = search || params?.search || params?.searchColumn || '';
    if (!searchTerm || searchTerm.length < 2) {
      return Promise.resolve({
        data: { results: [], count: 0 },
      });
    }
    return Promise.all([
      getUsersCustom({
        // limit: 25,
        ...GET_ALL_QUERY_PARAMS,
        limit: 25,
        userRole: USER_TYPES_VALUES.CLIENT,
        search: searchTerm,
        ...params,
      }),
      getContactTypeList({
        ...GET_ALL_QUERY_PARAMS,
      }),
    ]).then(([userRes, contactTypesRes]) => {
      // if(successResponse?.data){
      //   userRes.data.unshift(successResponse.data)
      // }
      const filteredUsers = userRes?.data?.filter(item => item?.clientType === CLIENT_TYPES_VALUES.INDIVIDUAL) || [];
      const contactsId = filteredUsers.map(item => item.id) || [];
      const contactsData = getValues('contacts')?.map(contact => contact?.id) || [];
      const tempInitial = contactsId.reduce((acc, val, i) => {
        if (contactsData.includes(val)) {
          //  acc = {...acc,i:true}
          acc[i] = true;
        }
        return acc;
      }, {});
      setInitialSelectedRows(tempInitial);

      return {
        ...userRes,
        data: {
          // ...userRes.data,
          results: filteredUsers.map(item => {
            // setData((old) => [...old, {
            //   ...item,
            //   name: `${item?.firstName} ${item?.lastName}`,
            //   email: item?.email,
            //   companyName: item?.companyName,
            // }])
            return {
              ...item,
              // contactType: item.contactType
              // ? +item.contactType
              // : contactTypesRes?.data?.results[0]?.id,
              name: [item?.firstName, item?.lastName].filter(Boolean).join(' '),
              email: item?.email,
              companyName: item?.companyName || item?.parentCompany?.companyName || '',
            };
          }),
        },
      };
    });
  };

  const [users, setUsers] = React.useState([]);
  const [selectedContact, setSelectedContact] = React.useState(null);
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isContactFormSubmitting,
    setIsContactFormSubmitting,
  ] = React.useState(false);

  React.useEffect(() => {
    setUsers(contacts);
  }, [contacts]);

  React.useEffect(() => {
    HandleAddContactModal(addUserModal && !addNewUserModalOpen);
  }, [addNewUserModalOpen, addUserModal]);

  React.useEffect(() => {
    if (isApiSuccess) {
      setAddNewUserModalOpen(false);
    }
  }, [isApiSuccess]);

  // creating the onSelect conatct function
  const handleContactSelect = (newContacts, ids, data) => {
    setSelectedContact(newContacts);
    const contacts = getValues('contacts');
    let tempContact;
    if (data) {
      if (contacts.findIndex(contact => contact.id === data.id) === -1) {
        tempContact = [...contacts];
        tempContact.push(data);
        setValue('contacts', tempContact);
      } else if (newContacts.findIndex(contact => contact.id === data.id) === -1) {
        tempContact = contacts.filter(contact => contact.id !== data.id);
        setValue('contacts', tempContact);
      }
    }
  };

  const handleAddContact = () => {
    setIsAddContactBtnLoading(true);
    // setValue("contacts",[...selectedContact, ...users])
    // const data=getValues("contacts")
    const _data = getValues('contacts');
    const uniqData = _data.filter((v, i, a) => a.findIndex(v2 => v2.id === v.id) === i);
    const projectContacts = uniqData.filter(c => !!c?.id);

    // Promise.all([updateProject({id: initialValues?.id, contacts: contactsData}), getUsersByProject(initialValues?.id)]).then(([projectUsers, ]))

    updateProject({ id: initialValues?.id, projectContacts }).then(res => {
      // getUsersByProject(initialValues?.id).then((projectUsers) => {
      setIsAddContactBtnLoading(false);
      setAddUserModal(false);
      setValue('contacts', uniqData);
      setInitialContact(getValues('contacts'));
    });
  };

  // Calling the user data api on click of contacts dropdown
  // const handleContactsDropdownClick = () => {
  //   Promise.all([getUsersCustom({userRole: USER_TYPES_VALUES.CLIENT,})]).then(([allClients]) => {
  //     setUsers(allClients?.data)
  //   })
  // }
  React.useEffect(() => {
    if (modalSuccess && isContactFormSubmitting) {
      Promise.all([
        getUsersCustom({
          ...GET_ALL_QUERY_PARAMS,
          userRole: USER_TYPES_VALUES.CLIENT,
          clientType: 1,
        }),
        getUsersCustom({
          ...GET_ALL_QUERY_PARAMS,
          userRole: USER_TYPES_VALUES.CLIENT,
          clientType: 2,
        }),
      ])
        .then(([individualClientsResponse, companyClientsResponse]) =>
          setUsers([
            ...individualClientsResponse.data.results?.filter(el => el?.isActive),
            ...companyClientsResponse.data.results?.filter(el => el?.isActive),
          ]),
        )
        .catch(err => console.log(err))
        .finally(() => {
          setIsContactFormSubmitting(false);
        });
    }
  }, [modalSuccess]);

  React.useEffect(() => {
    if (formState.isSubmitSuccessful && !Object.keys(errors).length) {
      dispatch(apiSlice.util.resetApiState());
      updateTable();
      setTimeout(() => {
        handleCloseEditProject();
      }, 300);
    }
  }, [formState.isSubmitSuccessful]);

  React.useEffect(() => {
    setValue('manager', initialValues?.manager?.id);
  }, []);

  const handlePlaceSelect = (address, isSelect) => {
    if (address || isSelect) {
      clearErrors('address');
    }
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        if (key === 'state') {
          const match = US_STATES.find(
            s => s.label.toLowerCase() === String(value).toLowerCase() || s.value.toLowerCase() === String(value).toLowerCase(),
          );
          return setValue(key, match?.value || value);
        }
        return setValue(key, value);
      });
    }
    return setValue('address', address);
  };

  const middleware = (submitFn: (data) => void) => data => {
    const contactList = (data.contacts || []).filter(c => !!c?.id);
    return submitFn({
      id: initialValues?.id,
      name: data.name,
      address: data.address,
      city: data.city,
      state: data.state,
      street: data.street,
      zipCode: data.zipCode,
      jurisdiction: data.jurisdiction.id,
      manager: data.manager.id,
      projectContacts: contactList,
      projectType: data.project_type,
      eboxConfirmationNumber: data.eboxConfirmationNumber || null,
    });
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const watchedType = watch('project_type');
  const [currentProjectType, setCurrentProjectType] = React.useState(watchedType);

  // Keep project type radio selection synced with form state
  React.useEffect(() => {
    setCurrentProjectType(watchedType);
  }, [watchedType]);

  if (!editMode) {
    return <ProjectDetails initial={initialValues} changeEditMode={changeMode} hideButtons={hideButtons} />;
  }

  return (
    <>
      <InputBaseContainer hidden>
        <Controller name="hidden" as={InputTextWithLabel} control={control} defaultValue="" isRequired={false} />
      </InputBaseContainer>
      {/* Full-width top */}
      <InputBaseContainer>
        <Controller
          as={InputTextWithLabel}
          name="name"
          control={control}
          helperText={errors.name?.message}
          error={errors.name?.message}
          label={labels.name}
          isRequired={isYupFieldRequired('name', validationSchema)}
        />
      </InputBaseContainer>
      <StyledInputContainer>
        <Controller
          name="contacts"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              isMulti
              showCompany
              selectAllActive
              placeholder=""
              label={labels.contacts}
              helperText={errors.contacts?.message}
              error={errors.contacts?.message}
              getOptionValue={(item: any) => (item.value ? item.value : item.id || Math.random())}
              getOptionLabel={(item: any) => {
                const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
                return [name, item?.email, item?.companyName].filter(Boolean).join(' - ');
              }}
              isRequired={isYupFieldRequired('contacts', validationSchema)}
              options={users}
              {...props}
            />
          )}
        />
        <ButtonContainer>
          <StyledButtonMain
            variant="outlined"
            onClick={() => {
              setAddUserModal(true);
              setInitialContact(getValues('contacts'));
            }}
          >
            Add Contact
          </StyledButtonMain>
        </ButtonContainer>
      </StyledInputContainer>

      {/* Two-column section */}
      <EditTwoCol>
        <EditCol>
          <InputBaseContainer>
            <Controller
              name="address"
              as={InputAutocompletePlaces}
              defaultValue=""
              control={control}
              helperText={errors.address?.message}
              error={errors.address?.message}
              label={labels.address}
              handleFieldChange={handlePlaceSelect}
              isRequired={isYupFieldRequired('address', validationSchema)}
            />
          </InputBaseContainer>
          <InputBaseContainer>
            <Controller
              as={InputTextWithLabel}
              name="street"
              control={control}
              helperText={errors.street?.message}
              error={errors.street?.message}
              label={labels.street}
              isRequired={isYupFieldRequired('street', validationSchema)}
            />
            <Controller
              as={InputTextWithLabel}
              name="city"
              control={control}
              helperText={errors.city?.message}
              error={errors.city?.message}
              label={labels.city}
              isRequired={isYupFieldRequired('city', validationSchema)}
            />
          </InputBaseContainer>
          <InputBaseContainer>
            <Controller
              name="state"
              defaultValue=""
              control={control}
              render={({ onChange, onBlur, value }) => {
                const selected = US_STATES.find(s => s.value === value || s.label === value) || null;
                return (
                  <InputSelectWithLabel
                    placeholder="Select State"
                    label={labels.state}
                    error={errors.state}
                    isRequired={isYupFieldRequired('state', validationSchema)}
                    isSearchable
                    options={US_STATES}
                    value={selected}
                    onBlur={onBlur}
                    onChange={opt => onChange(opt?.value || '')}
                  />
                );
              }}
            />
            <Controller
              name="zipCode"
              defaultValue=""
              control={control}
              render={({ onChange, onBlur, value }) => (
                <InputTextWithLabel
                  name="zipCode"
                  label={labels.zipCode}
                  error={errors.zipCode}
                  isRequired={isYupFieldRequired('zipCode', validationSchema)}
                  value={value}
                  onBlur={onBlur}
                  onChange={e => {
                    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                    let formatted = digits;
                    if (digits.length > 5) {
                      formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
                    }
                    onChange(formatted);
                  }}
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  placeholder="55555 or 55555-5555"
                />
              )}
            />
          </InputBaseContainer>
        </EditCol>
        <EditCol>
          <InputBaseContainer>
            <Controller
              name="jurisdiction"
              defaultValue=""
              control={control}
              render={({ onChange, value, ...rest }) => (
                <InputSelectWithLabel
                  placeholder=""
                  label={labels.jurisdiction}
                  helperText={errors.jurisdiction?.message}
                  error={errors.jurisdiction?.message}
                  getOptionValue={(item: any) => item.id || Math.random()}
                  getOptionLabel={(item: any) => item.name}
                  isRequired={isYupFieldRequired('jurisdiction', validationSchema)}
                  options={jurisdiction}
                  value={value}
                  isLoading={checkingJurisdiction}
                  onChange={selected => handleJurisdictionSelect(selected, onChange)}
                  {...rest}
                />
              )}
            />
          </InputBaseContainer>
          <InputBaseContainer>
            <Controller
              name="manager"
              defaultValue=""
              control={control}
              render={props => (
                <InputSelectWithLabel
                  label={labels.manager}
                  helperText={errors.manager?.message}
                  error={errors.manager?.message}
                  options={manager}
                  getOptionValue={(item: any) => item.id || Math.random()}
                  getOptionLabel={(item: any) => `${item?.firstName} ${item?.lastName}`}
                  isRequired={isYupFieldRequired('manager', validationSchema)}
                  {...props}
                />
              )}
            />
          </InputBaseContainer>
          <ProjectTypeField>
            <ProjectTypeLabel>
              Project Type
              {isYupFieldRequired('project_type', validationSchema) && (
                <span style={{ color: 'var(--danger-500)', marginLeft: '2px' }}>*</span>
              )}
            </ProjectTypeLabel>
            <RadioGroup>
              {[
                { value: '1_2_FAMILY', label: '1 & 2 Family Dwelling' },
                { value: 'MULTIFAMILY', label: 'Multifamily' },
                { value: 'COMMERCIAL', label: 'Commercial' },
              ].map(option => (
                <RadioOption
                  key={option.value}
                  $selected={currentProjectType === option.value}
                  onClick={() => {
                    setValue('project_type', option.value);
                    setCurrentProjectType(option.value);
                    trigger('project_type');
                  }}
                >
                  <PillDot $selected={currentProjectType === option.value} />
                  {option.label}
                </RadioOption>
              ))}
            </RadioGroup>
            {errors.project_type?.message && <ProjectTypeError>{errors.project_type.message}</ProjectTypeError>}
          </ProjectTypeField>
        </EditCol>
      </EditTwoCol>
      <EboxLookup
        value={watch('eboxConfirmationNumber') || ''}
        onChange={val => setValue('eboxConfirmationNumber', val)}
        projectAddress={[watch('street'), watch('city'), watch('state'), watch('zipCode')].filter(Boolean).join(', ')}
      />
      <ButtonContainer>
        <ButtonWithLoader
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSubmit(middleware(submitWithServerErrors))}
          disabled={!!Object.keys(errors).length}
          loading={formState.isSubmitting && !Object.keys(errors).length}
        >
          Save
        </ButtonWithLoader>
      </ButtonContainer>

      {jurisdictionWarningOpen && (
        <JurisdictionChangeConfirmModal onConfirm={confirmJurisdictionChange} onCancel={cancelJurisdictionChange} />
      )}

      {addUserModal && (
        <StyledModalDraggable
          onClose={() => {
            if (addNewUserModalOpen) {
              setAddNewUserModalOpen(false);
              setAddContactModalTitle('Add Contacts');
            } else {
              setAddUserModal(false);
              setAddContactModalTitle('Add Contacts');
              setValue('contacts', initialContact);
            }
          }}
          title={addContactModalTitle}
          disabledDraggable
          wide
          clientType={clientType}
          addContactModal={addUserModal && !addNewUserModalOpen}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!addNewUserModalOpen && (
              <>
                <ActionsPanel
                  isBreadCrumbsVisible={false}
                  onSearch={setSearch}
                  isActionPanelMoreVisible={false}
                  className="action-container"
                />
              </>
            )}
            {!addNewUserModalOpen && (
              <>
                <ButtonWithLoader
                  style={{ flexShrink: 0 }}
                  variant="contained"
                  color="primary"
                  onClick={() => setAddNewUserModalOpen(true)}
                  loading={false}
                >
                  New
                </ButtonWithLoader>
                <br />
              </>
            )}
          </div>
          {addNewUserModalOpen && (
            <AddContactWizard
              modalError={modalErrorTitle.length > 0}
              initialErrors={innerModalErrors}
              onSubmit={createOrUpdate}
              setModalTitle={setAddContactModalTitle}
            />
          )}
          {(isApiSuccess || modalError) && (
            <ModalInfo title="Success" timeToClose={5000} isError={modalError} onClose={handleCloseModalInfo} errorMsg={modalErrorTitle} />
          )}

          {/* <InfiniteScroll
      dataLength={newData.length}
      next={fetchMoreData}
      height={800}
      hasMore={true}
      loader={<h4>Loading...</h4>}
    > */}
          {!addNewUserModalOpen && (!search || search.length < 2) && (
            <SearchHint>
              <SearchHintTitle>Start typing to find contacts</SearchHintTitle>
              <SearchHintBody>
                Enter at least <strong>2 characters</strong> in the search box above to look up contacts by name or email.
              </SearchHintBody>
            </SearchHint>
          )}
          {!addNewUserModalOpen && search && search.length >= 2 && (
            <>
              <ContactSearchList
                searchQuery={search}
                selectedIds={(getValues('contacts') || []).map((c: any) => c?.id).filter(Boolean)}
                onToggle={contact => {
                  const current = getValues('contacts') || [];
                  const isSelected = current.some((c: any) => c?.id === contact.id);
                  const next = isSelected ? current.filter((c: any) => c?.id !== contact.id) : [...current, contact];
                  setValue('contacts', next);
                  setSelectedContact(next);
                }}
              />
              <br />
              <ButtonWithLoader
                variant="outlined"
                // color="primary"
                onClick={() => setAddUserModal(false)}
                loading={false}
              >
                Cancel
              </ButtonWithLoader>{' '}
              <ButtonWithLoader
                variant="contained"
                color="primary"
                // type="submit"
                // style={{marginLeft: ''}}
                onClick={() => handleAddContact()}
                // className={classes.button}
                // disabled={
                //   !formState.isDirty ||
                //   !!Object.keys(errors).length
                // }
                loading={false}
              >
                Ok
              </ButtonWithLoader>
            </>
          )}
          {/* </InfiniteScroll> */}
        </StyledModalDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo timeToClose={5000} isError={modalError} onClose={handleCloseModalInfo} errorMsg={modalErrorTitle} />
      )}
    </>
  );
};

interface Props {
  editMode: boolean;
  initialValues: any;
  changeMode: (data: boolean) => void;
  jurisdiction: any;
  manager: Array<UserType>;
  contacts: any;
  handleCloseEditProject: () => void;
  updateTable: () => void;
  hideButtons?: boolean;
}

const StyledButtonMain = styled(ButtonMain)`
  &.MuiButton-root {
    line-height: 1.15;
  }
`;

// height: 100%;
// overflow: inherit;
const StyledModalDraggable = styled(ModalDefaultDraggable)`
  overflow-y: auto;
  text-align: center;
`;

const EditTwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0 var(--space-6);
  align-items: start;
`;

const EditCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ProjectTypeField = styled.div`
  padding: var(--space-2) 0;
  width: 100%;
`;

const ProjectTypeLabel = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
  margin-bottom: var(--space-3);
`;

const RadioGroup = styled.div`
  display: flex;
  gap: var(--space-2);
  flex-wrap: nowrap;
`;

const RadioOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary-600)' : 'var(--neutral-300)')};
  background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'var(--neutral-0)')};
  cursor: pointer;
  font-size: var(--fs-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
  transition: border-color var(--motion-fast) var(--ease-standard), background-color var(--motion-fast) var(--ease-standard);
  &:hover {
    border-color: ${({ $selected }) => ($selected ? 'var(--primary-600)' : 'var(--neutral-400)')};
  }
`;

const PillDot = styled.div<{ $selected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: ${({ $selected }) => ($selected ? 'var(--primary-600)' : 'var(--neutral-300)')};
  flex-shrink: 0;
`;

const ProjectTypeError = styled.div`
  color: var(--danger-500);
  font-size: var(--fs-sm);
  margin-top: var(--space-2);
`;

const StyledInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding-top: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
`;

const SearchHint = styled.div`
  text-align: center;
  padding: 32px 16px;
  margin-top: 12px;
  background: var(--neutral-50);
  border: 1px dashed var(--border);
  border-radius: 10px;
  color: var(--neutral-600);
`;

const SearchHintTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 6px;
`;

const SearchHintBody = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--neutral-600);

  strong {
    color: var(--mainText);
  }
`;

export default ProjectEditForm;
